import { useCallback, useId, useRef, type KeyboardEvent } from 'react';
import { cn } from './cn';

export interface TabItem {
  id: string;
  label: string;
}

/**
 * The "ALL / STATE / NATIONAL / …" filter strip from PRD §9.
 *
 * Implements the WAI-ARIA tabs keyboard contract, which is the part a hand-rolled row of
 * buttons always misses: arrow keys move between tabs, Home and End jump to the ends, and
 * only the active tab is in the tab order, so a reader tabs *past* the strip rather than
 * through six buttons.
 */
export function Tabs({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Names the tablist for screen readers, e.g. "Filter latest news". */
  label: string;
  className?: string;
}) {
  const baseId = useId();
  const refs = useRef(new Map<string, HTMLButtonElement>());

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const index = items.findIndex((item) => item.id === value);
      if (index < 0) return;

      const move = (next: number) => {
        event.preventDefault();
        const target = items[(next + items.length) % items.length];
        if (!target) return;
        onChange(target.id);
        refs.current.get(target.id)?.focus();
      };

      if (event.key === 'ArrowRight') move(index + 1);
      if (event.key === 'ArrowLeft') move(index - 1);
      if (event.key === 'Home') move(0);
      if (event.key === 'End') move(items.length - 1);
    },
    [items, onChange, value],
  );

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn('flex items-center gap-5 overflow-x-auto', className)}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            ref={(node) => {
              if (node) refs.current.set(item.id, node);
              else refs.current.delete(item.id);
            }}
            role="tab"
            id={`${baseId}-${item.id}`}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(item.id)}
            className={cn(
              'relative shrink-0 whitespace-nowrap py-1 text-base font-bold transition',
              active ? 'text-red' : 'text-muted hover:text-ink',
              active &&
                'after:absolute after:-bottom-1.5 after:left-0 after:right-0 after:h-0.5 after:bg-red',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
